// =============================================================
// noteRouting — reading the files in a Notes-app note, and filing the note onto
// deals. ONE copy, used by the web Notes app (frontend/src/pages/NotesApp.jsx)
// and the iOS editor (mobile/src/notebook/NotebookEditor.tsx).
//
// CONVERT HANDWRITING TO TEXT: a PDF or photo in the note is read by the
// server (POST /api/notebook-files/{id}/ocr) and the text goes right under the
// file, headed "Transcribed from <file> by Harvi — …" so nobody takes it for
// typed text. `convertFileBlock` places it; `readFile` is the caller's (it
// holds the token).
//
// FILING THE NOTE (Suggest links & tasks · Split & route): the files are part
// of the note, so they are READ FIRST — any PDF or photo not yet transcribed is
// converted into the note — and the text sent to the router is the note with
// those transcriptions in it. The file blocks themselves are left out of that
// text (a reference means nothing to the router) and handed back as `files`,
// each with the text read from it, so a split can attach each file to the
// routed notes it fed (`assignFilesToRows`).
//
// The editor is duck-typed (a BlockNote editor): document, getBlock,
// insertBlocks, tryParseMarkdownToBlocks, blocksToMarkdownLossy.
// =============================================================

import { notebookFileIdFromUrl } from "./notebookFiles";

const READABLE_EXT = new Set(["pdf", "jpg", "jpeg", "png", "bmp", "tif", "tiff", "heic", "heif"]);
const extOf = (name: unknown): string => {
  const m = /\.([a-z0-9]+)$/i.exec(String(name || ""));
  return m ? m[1].toLowerCase() : "";
};

/** A file stored in a note (any type): its FileID, or null. */
export function noteFileIdOf(block: any): string | null {
  if (!block || !["file", "image", "video", "audio"].includes(block.type)) return null;
  return notebookFileIdFromUrl(block.props?.url);
}

/** The FileID to read, or null when this block is not a PDF/photo stored in the note. */
export function convertibleFileId(block: any): string | null {
  if (!block || !["file", "image"].includes(block.type)) return null;
  const fileId = notebookFileIdFromUrl(block.props?.url);
  if (!fileId) return null;
  const ext = extOf(block.props?.name);
  // A file block must say it is a PDF or a photo; an image block with no
  // extension is still a photo (a pasted screenshot is named "image").
  if (block.type === "file") return READABLE_EXT.has(ext) ? fileId : null;
  return !ext || READABLE_EXT.has(ext) ? fileId : null;
}

export const TRANSCRIBED_PREFIX = "Transcribed from ";

const fileLabel = (name: unknown) => String(name || "").trim() || "the file";

/** The italic line placed above a transcription. */
export function transcriptionHeader(fileName: unknown) {
  return {
    type: "paragraph",
    content: [{ type: "text", text: `${TRANSCRIBED_PREFIX}${fileLabel(fileName)} by Harvi — check names and figures against the file.`, styles: { italic: true } }],
  };
}

const blockText = (b: any): string => (Array.isArray(b?.content) ? b.content.map((c: any) => c?.text || "").join("") : "");

/** True when the block right after this file already heads a transcription of it. */
export function alreadyTranscribed(document: any[], block: any): boolean {
  const blocks = Array.isArray(document) ? document : [];
  const i = blocks.findIndex((b) => b?.id === block?.id);
  if (i < 0 || i + 1 >= blocks.length) return false;
  return blockText(blocks[i + 1]).startsWith(`${TRANSCRIBED_PREFIX}${fileLabel(block?.props?.name)}`);
}

/**
 * The text transcribed from this file: the blocks after its header, up to the
 * next file or the next transcription. '' when it has not been converted.
 */
export function transcriptionTextFor(document: any[], block: any): string {
  const blocks = Array.isArray(document) ? document : [];
  const i = blocks.findIndex((b) => b?.id === block?.id);
  if (i < 0 || !alreadyTranscribed(blocks, block)) return "";
  const out: string[] = [];
  for (let j = i + 2; j < blocks.length; j++) {
    const b = blocks[j];
    if (noteFileIdOf(b) || blockText(b).startsWith(TRANSCRIBED_PREFIX)) break;
    out.push(blockText(b));
  }
  return out.join("\n").trim();
}

export interface NoteFileRef { blockId: string; fileId: string; name: string; convertible: boolean }

/** Every file stored in the note, top level first, then nested. */
export function noteFilesIn(document: any[]): NoteFileRef[] {
  const out: NoteFileRef[] = [];
  const seen = new Set<string>();
  const walk = (blocks: any[]) => {
    for (const b of Array.isArray(blocks) ? blocks : []) {
      const fileId = noteFileIdOf(b);
      if (fileId && !seen.has(fileId)) {
        seen.add(fileId);
        out.push({ blockId: b.id, fileId, name: fileLabel(b.props?.name), convertible: !!convertibleFileId(b) });
      }
      if (b?.children?.length) walk(b.children);
    }
  };
  walk(document);
  return out;
}

export type ReadFile = (fileId: string) => Promise<{ markdown?: string | null; text?: string | null }>;

async function markdownToBlocks(editor: any, body: string): Promise<any[]> {
  let blocks: any[] = [];
  try { blocks = await editor.tryParseMarkdownToBlocks(body); } catch { blocks = []; }
  if (!blocks?.length) blocks = body.split("\n").map((line) => ({ type: "paragraph", content: line }));
  return blocks;
}

/**
 * Read one file's handwriting into the note, under the file. Resolves
 * 'added' · 'empty' (no writing found). Throws the reader's error.
 */
export async function convertFileBlock(editor: any, block: any, readFile: ReadFile): Promise<"added" | "empty"> {
  const fileId = convertibleFileId(block);
  if (!fileId) return "empty";
  const r = await readFile(fileId);
  const body = String(r?.markdown && String(r.markdown).trim() ? r.markdown : (r?.text || "")).trim();
  if (!body) return "empty";
  const blocks = await markdownToBlocks(editor, body);
  // The file may have moved or gone while the read ran; then the text goes at the end.
  const anchor = editor.getBlock(block.id) || editor.document[editor.document.length - 1];
  editor.insertBlocks([transcriptionHeader(block.props?.name), ...blocks], anchor, "after");
  return "added";
}

const FILE_REF = /harvus-file:\/\/notebook\//i;

/** The note's Markdown with file blocks removed, and how many were removed. */
export function noteMarkdownForRouting(markdown: unknown): { text: string; filesLeft: number } {
  let filesLeft = 0;
  const lines = String(markdown || "").split("\n").filter((line) => {
    if (FILE_REF.test(line)) { filesLeft += 1; return false; }
    return true;
  });
  return { text: lines.join("\n").replace(/\n{3,}/g, "\n\n").trim(), filesLeft };
}

export interface RoutingFile { fileId: string; name: string; text: string; read: boolean }
export interface RoutingBundle {
  text: string;
  files: RoutingFile[];
  /** Files that could not be read (not a PDF/photo, or the read failed), with the reason. */
  unread: { name: string; reason: string }[];
}

/**
 * Everything the router needs: converts any PDF/photo not yet transcribed INTO
 * the note (so the original keeps the text and it can be checked), then
 * returns the note's text and its files with what each one said.
 */
export async function prepareNoteForRouting(editor: any, { readFile, onStatus }: { readFile: ReadFile; onStatus?: (s: string) => void }): Promise<RoutingBundle> {
  const unread: { name: string; reason: string }[] = [];
  const blockFor = (id: string) => { try { return editor.getBlock(id); } catch { return null; } };
  for (const f of noteFilesIn(editor.document)) {
    if (!f.convertible) { unread.push({ name: f.name, reason: "not a PDF or photo — it is attached, not read" }); continue; }
    const block = blockFor(f.blockId);
    if (!block || alreadyTranscribed(editor.document, block)) continue;
    onStatus?.(`Reading ${f.name}…`);
    try {
      const r = await convertFileBlock(editor, block, readFile);
      if (r === "empty") unread.push({ name: f.name, reason: "no writing was found in it" });
    } catch (e: any) {
      unread.push({ name: f.name, reason: e?.message || "it could not be read" });
    }
  }
  const doc = editor.document;
  const files: RoutingFile[] = noteFilesIn(doc).map((f) => {
    const block = blockFor(f.blockId);
    const text = block ? transcriptionTextFor(doc, block) : "";
    return { fileId: f.fileId, name: f.name, text, read: !!text };
  });
  let markdown = "";
  try { markdown = await editor.blocksToMarkdownLossy(doc); } catch { markdown = ""; }
  return { text: noteMarkdownForRouting(markdown).text, files, unread };
}

// ── Suggest links & tasks ───────────────────────────────────────────────────

const lc = (v: unknown) => String(v || "").toLowerCase();

export interface LinkRef { type: "Deal" | "DealDetail"; id: string }

/** The link a routed item points at: the capital item when there is one, else the deal. */
export function linkRefFor(item: any): LinkRef | null {
  if (!item?.dealId) return null;
  return item.dealDetailId ? { type: "DealDetail", id: item.dealDetailId } : { type: "Deal", id: item.dealId };
}

export const LINK_DEFAULT_CONFIDENCE = 0.6;

export interface LinkSuggestion { key: string; ref: LinkRef; label: string; confidence: number | null; excerpts: string[]; checked: boolean }
export interface TaskSuggestion {
  key: string; title: string; description: string; dealId: string; dealDetailId: string | null; projectId: string | null;
  dueDate: string | null; priority: string | null; assigneeIds: string[]; label: string; sourceExcerpt: string; checked: boolean;
}

/**
 * Group a POST /api/dictation/parse result into link and task suggestions.
 *   links: one per record, best confidence first, excluding records the note
 *          is already linked to; ticked only when the router is sure.
 *   tasks: every task item with a deal; never ticked by default.
 */
export function buildSuggestions(res: { items?: any[]; catalog?: any[] } = {}, existingLinks: { type: string; id: string }[] = []) {
  const have = new Set((existingLinks || []).map((l) => `${l.type}:${lc(l.id)}`));
  const catalog = res?.catalog || [];
  const labelOf = (it: any) => {
    const c = Number.isInteger(it?.catalogIndex) ? catalog[it.catalogIndex] : null;
    return c?.label || c?.name || "Deal";
  };
  const byKey = new Map<string, LinkSuggestion>();
  const tasks: TaskSuggestion[] = [];
  for (const it of Array.isArray(res?.items) ? res.items : []) {
    const ref = linkRefFor(it);
    if (!ref) continue;
    if (it.type === "task") {
      tasks.push({
        key: `t${tasks.length}`,
        title: String(it.title || it.content || "").trim().slice(0, 300),
        description: it.content && String(it.content).trim() !== String(it.title || "").trim() ? String(it.content).trim() : "",
        dealId: it.dealId, dealDetailId: it.dealDetailId || null, projectId: it.projectId || null,
        dueDate: it.dueDate || null, priority: it.priority || null, assigneeIds: it.assigneeIds || [],
        label: labelOf(it), sourceExcerpt: it.sourceExcerpt || "", checked: false,
      });
    }
    const k = `${ref.type}:${lc(ref.id)}`;
    if (have.has(k)) continue;
    const conf = typeof it.confidence === "number" ? it.confidence : null;
    const cur = byKey.get(k);
    if (!cur) {
      byKey.set(k, { key: k, ref, label: labelOf(it), confidence: conf, excerpts: it.sourceExcerpt ? [it.sourceExcerpt] : [], checked: false });
    } else {
      if (conf != null && (cur.confidence == null || conf > cur.confidence)) cur.confidence = conf;
      if (it.sourceExcerpt && cur.excerpts.length < 2) cur.excerpts.push(it.sourceExcerpt);
    }
  }
  const links = [...byKey.values()]
    .sort((a, b) => (b.confidence ?? -1) - (a.confidence ?? -1))
    .map((l) => ({ ...l, checked: l.confidence != null && l.confidence >= LINK_DEFAULT_CONFIDENCE }));
  return { links, tasks };
}

// ── Split & route: which routed notes carry which file ──────────────────────

const words = (s: unknown): string[] => String(s || "").toLowerCase().match(/[a-z0-9$%.,]{3,}/g)?.map((w) => w.replace(/[.,]+$/, "")).filter(Boolean) || [];

/** Did this routed section come from this file's text? Unknown text matches nothing. */
export function sectionFromFile(fileText: unknown, section: { sourceExcerpt?: unknown; content?: unknown }): boolean {
  const fw = new Set(words(fileText));
  if (!fw.size) return false;
  const probe = words(section.sourceExcerpt).length >= 3 ? words(section.sourceExcerpt) : words(String(section.content || "").slice(0, 400));
  if (probe.length < 3) return false;
  const hit = probe.filter((w) => fw.has(w)).length;
  return hit / probe.length >= 0.6;
}

/**
 * For each NOTE row, the files to attach (one entry per file, `include` set):
 * a file goes with the sections that came from its text; a file none of them
 * came from (an unread file, or one whose text did not route) goes with every
 * note, so it is never silently dropped. Tasks carry no files.
 */
export function assignFilesToRows<R extends { type: string; sourceExcerpt?: unknown; content?: unknown }>(
  files: RoutingFile[], rows: R[],
): { fileId: string; name: string; include: boolean }[][] {
  const matches = files.map((f) => rows.map((r) => r.type === "note" && sectionFromFile(f.text, r)));
  return rows.map((r, ri) => (r.type === "note"
    ? files.map((f, fi) => ({ fileId: f.fileId, name: f.name, include: matches[fi][ri] || !matches[fi].some(Boolean) }))
    : files.map((f) => ({ fileId: f.fileId, name: f.name, include: false }))));
}
